/**
 * Iter 247 — MemberHoverCard : popup d'aperçu rapide au hover sur une carte membre.
 *
 * Affiche les 5 derniers clients assignés + 3 derniers changements de permissions
 * + indicateur dernière connexion. Permet à l'admin de trier sans cliquer.
 *
 * Utilisation :
 *   <MemberHoverCard userId={m.user_id}>
 *     <Card> ... </Card>
 *   </MemberHoverCard>
 *
 * Fetch lazy : la première hover déclenche la requête ; cache pendant la session.
 */
import { useState, useRef, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Users, Clock, Shield, Briefcase } from "lucide-react";

// Module-level cache to avoid refetching on subsequent hovers in the same session.
const cache = new Map();

export default function MemberHoverCard({ userId, children, disabled = false }) {
  const { axiosAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (cache.has(userId)) {
      setData(cache.get(userId));
      return;
    }
    setLoading(true);
    try {
      const r = await axiosAuth().get(`/admin/users/${userId}/recent-activity`);
      cache.set(userId, r.data);
      setData(r.data);
    } catch (e) {
      cache.set(userId, { error: true });
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }, [userId, axiosAuth]);

  const handleEnter = () => {
    if (disabled) return;
    // 400ms debounce to avoid firing on quick mouseovers
    timerRef.current = setTimeout(() => {
      setOpen(true);
      if (!data) fetchData();
    }, 400);
  };

  const handleLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(false);
  };

  if (disabled) return children;

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid={`hover-card-wrapper-${userId}`}
    >
      {children}
      {open && (
        <div
          className="absolute left-0 top-full mt-2 z-50 w-[360px] bg-[#14101c] border border-cyan-400/30 rounded-xl shadow-2xl p-4 animate-fade-in"
          style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(34, 211, 238, 0.15)" }}
          data-testid="member-hover-popup"
        >
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
            </div>
          )}
          {data?.error && (
            <div className="text-xs text-slate-500 py-2">Aperçu indisponible</div>
          )}
          {data && !data.error && !loading && (
            <div className="space-y-3">
              {/* Header */}
              <div className="pb-2 border-b border-white/5">
                <div className="text-xs font-bold text-white">{data.user?.name || data.user?.email}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-slate-500">{data.user?.role}</span>
                  {data.user?.active === false && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">Inactif</span>
                  )}
                  {data.user?.last_login && (
                    <span className="text-[10px] text-slate-500">
                      <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                      {new Date(data.user.last_login).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                    </span>
                  )}
                </div>
              </div>

              {/* Recent clients */}
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                  <Briefcase className="w-3 h-3" />
                  Clients récents
                  {data.stats?.assigned_clients_count > 0 && (
                    <span className="text-cyan-400">({data.stats.assigned_clients_count})</span>
                  )}
                </div>
                {(!data.recent_clients || data.recent_clients.length === 0) ? (
                  <div className="text-xs text-slate-600 italic">Aucun client assigné</div>
                ) : (
                  <div className="space-y-1">
                    {data.recent_clients.map((c) => (
                      <div key={c.user_id} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-white/[0.02]">
                        <Users className="w-3 h-3 text-slate-500" />
                        <div className="flex-1 min-w-0 truncate">
                          <span className="text-white">{c.name || c.email}</span>
                          {c.company_name && <span className="text-slate-500 ml-1">· {c.company_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent permission changes */}
              {data.recent_permission_audits?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                    <Shield className="w-3 h-3" />
                    Derniers changements de permissions
                  </div>
                  <div className="space-y-1">
                    {data.recent_permission_audits.map((a, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-[11px] px-2 py-1 rounded bg-white/[0.02]">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="text-slate-300">{a.actor_email?.split("@")[0]}</span>
                          <span className="text-slate-600">→</span>
                          <span className="text-cyan-400">{a.changes_count} perm</span>
                          {a.preset_applied && (
                            <span className="px-1 py-0.5 rounded bg-[#f7c935]/15 text-[#f7c935] text-[9px] font-bold">
                              {a.preset_applied}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-500 whitespace-nowrap">
                          {new Date(a.timestamp).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
